import { loadFont } from "@remotion/google-fonts/Poppins";
import { AbsoluteFill, Img } from "remotion";

// Only the weights these compositions use, latin only. The unqualified call pulls every weight and
// subset, which is 54 network requests during a render that must not depend on the network.
const { fontFamily } = loadFont("normal", { weights: ["500", "600", "700"], subsets: ["latin"] });

/**
 * The Pinterest pin family. All 1000x1500 (2:3), which is the ratio the search grid does not crop.
 *
 * Four treatments rather than one, because a pin is found by search months after posting and a feed
 * of identical cards reads as a spreadsheet export. The first batch was one template fourteen times
 * and the sameness was the first thing anyone noticed.
 *
 * Each treatment carries a different amount of information, so the choice is driven by the angle:
 *
 *   pin-card  ranked bars      several destinations compared. Analytical. Use sparingly.
 *   pin-stat  one number       a single figure at size. Most stopping power, least information.
 *   pin-list  editorial list   a ranking without chart furniture. Suits trends.
 *   pin-duo   two panels       a contrast between exactly two places. Impossible as a bar chart.
 *
 * Values are strings on purpose in three of the four: these carry "+54%" and "2 nights" as readily
 * as a bare number, and formatting a percentage is the caller's job, not the composition's.
 */

const INK = "#0f3e4a";
const GREY = "#658c96";
const DEEP = "#071f26";

/** Shared by every pin: photo, dark wash, content on top. */
const Backdrop: React.FC<{ photo: string; gradient: string }> = ({ photo, gradient }) => (
	<>
		<Img src={photo} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
		<AbsoluteFill style={{ background: gradient }} />
	</>
);

export type PinCardProps = {
	/** The search query, close to verbatim. Pinterest is read by a search index before a person. */
	title: string;
	eyebrow?: string;
	rows: { name: string; value: number; note?: string }[];
	unit: string;
	photo: string;
};

/**
 * `pin-card` — ranked bars.
 *
 * Keeps the lesson the Instagram feed card taught: a photo can carry a headline, it cannot carry a
 * table. Title over the image, numbers on a solid panel below. Magnitude is lightness steps of one
 * hue with the value in ink beside its bar, never printed on the fill.
 */
export const PinCard: React.FC<PinCardProps> = ({ title, eyebrow, rows, unit, photo }) => {
	const fill = (n: number) => (n >= 4 ? "#0b8f68" : n >= 3 ? "#13c892" : "#86e0c4");
	const max = Math.max(...rows.map((r) => r.value));

	return (
		<AbsoluteFill style={{ fontFamily, backgroundColor: "#fff" }}>
			<div style={{ position: "relative", height: 600, overflow: "hidden" }}>
				<Backdrop photo={photo} gradient="linear-gradient(180deg, rgba(9,46,55,.52) 0%, rgba(9,46,55,.28) 40%, rgba(9,46,55,.86) 100%)" />
				<AbsoluteFill style={{ padding: "54px 58px 48px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
					{eyebrow ? (
						<div style={{ fontSize: 25, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#ffc14a" }}>{eyebrow}</div>
					) : (
						<div />
					)}
					<div style={{ fontSize: 78, lineHeight: 1.05, fontWeight: 700, color: "#fff", letterSpacing: -1.5 }}>{title}</div>
				</AbsoluteFill>
			</div>

			<div style={{ flex: 1, padding: "52px 58px 44px", display: "flex", flexDirection: "column" }}>
				{/* Rows spread through the panel rather than stacking. A fixed gap left a dead band above
				    the wordmark, which a pin competing in a search grid cannot afford. */}
				<div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
					{rows.map((r) => (
						<div key={r.name} style={{ display: "grid", gridTemplateColumns: "290px 1fr 130px", alignItems: "center", gap: 22 }}>
							<div style={{ fontSize: 34, fontWeight: 500, color: INK }}>
								{r.name}
								{r.note ? <div style={{ fontSize: 21, color: GREY, fontWeight: 400 }}>{r.note}</div> : null}
							</div>
							<div style={{ height: 30, borderRadius: 8, background: "#edf5f7", overflow: "hidden" }}>
								<div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: fill(r.value), borderRadius: 8 }} />
							</div>
							<div style={{ fontSize: 40, fontWeight: 700, color: INK, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
								{r.value}
								<span style={{ fontSize: 24, fontWeight: 500, color: GREY }}> {unit}</span>
							</div>
						</div>
					))}
				</div>
				<div style={{ fontSize: 26, fontWeight: 600, letterSpacing: 1, color: GREY, marginTop: 40 }}>stippl.io</div>
			</div>
		</AbsoluteFill>
	);
};

export type StatPinProps = {
	eyebrow: string;
	/** A string, so this carries "38%" as happily as "2". */
	value: string;
	lede: string;
	footnote: string;
	photo: string;
};

/**
 * `pin-stat` — one number, full bleed.
 *
 * The most stopping power and the least information, which is the trade. Nothing here needs parsing
 * at thumbnail size: a figure and one sentence saying what it means. Reach for it when the number is
 * surprising on its own, not when it needs company to make sense.
 */
export const StatPin: React.FC<StatPinProps> = ({ eyebrow, value, lede, footnote, photo }) => (
	<AbsoluteFill style={{ fontFamily, backgroundColor: DEEP }}>
		<Backdrop photo={photo} gradient="linear-gradient(180deg, rgba(7,31,38,.3) 0%, rgba(7,31,38,.55) 46%, rgba(7,31,38,.95) 100%)" />
		<AbsoluteFill style={{ padding: "96px 70px 70px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
			<div style={{ fontSize: 34, fontWeight: 600, letterSpacing: 6, textTransform: "uppercase", color: "#ffc14a", marginBottom: 10 }}>
				{eyebrow}
			</div>
			<div style={{ fontSize: 440, lineHeight: 0.78, fontWeight: 700, color: "#fff", letterSpacing: -16, fontVariantNumeric: "tabular-nums" }}>
				{value}
			</div>
			<div style={{ fontSize: 60, lineHeight: 1.14, fontWeight: 600, color: "#fff", marginTop: 26, maxWidth: 820 }}>{lede}</div>
			<div
				style={{
					marginTop: 56,
					paddingTop: 22,
					borderTop: "2px solid rgba(255,255,255,.2)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					fontSize: 26,
					color: "rgba(255,255,255,.66)",
				}}
			>
				<span>{footnote}</span>
				<b style={{ color: "#fff", fontWeight: 600 }}>stippl.io</b>
			</div>
		</AbsoluteFill>
	</AbsoluteFill>
);

export type ListPinProps = {
	eyebrow: string;
	title: string;
	rows: { name: string; value: string }[];
	photo: string;
};

/**
 * `pin-list` — editorial list on a warm ground.
 *
 * A ranking without chart furniture. The warm paper deliberately leaves the brand's teal-on-white
 * for this one format: on Pinterest the competition is magazine-styled, and a page that looks like a
 * report loses to one that looks like something torn out and kept.
 *
 * Values are strings so a growth figure keeps its sign and a night count keeps its unit.
 */
export const ListPin: React.FC<ListPinProps> = ({ eyebrow, title, rows, photo }) => (
	<AbsoluteFill style={{ fontFamily, backgroundColor: "#f6efe3" }}>
		<div style={{ position: "relative", height: 520, overflow: "hidden" }}>
			<Backdrop photo={photo} gradient="linear-gradient(180deg, rgba(20,40,36,.12) 0%, rgba(246,239,227,.9) 100%)" />
		</div>
		<div style={{ flex: 1, padding: "16px 66px 52px", display: "flex", flexDirection: "column" }}>
			<div style={{ fontSize: 24, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", color: "#c9861f" }}>{eyebrow}</div>
			<div style={{ fontSize: 76, lineHeight: 1.04, fontWeight: 700, color: "#173b36", margin: "18px 0 30px", letterSpacing: -1.6 }}>{title}</div>
			<div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
				{rows.map((r) => (
					<div
						key={r.name}
						style={{
							display: "flex",
							alignItems: "baseline",
							justifyContent: "space-between",
							borderBottom: "2px solid rgba(23,59,54,.14)",
							paddingBottom: 20,
						}}
					>
						<span style={{ fontSize: 54, fontWeight: 600, color: "#173b36" }}>{r.name}</span>
						<span style={{ fontSize: 62, fontWeight: 700, color: "#0b8f68", fontVariantNumeric: "tabular-nums" }}>{r.value}</span>
					</div>
				))}
			</div>
			<div style={{ fontSize: 26, fontWeight: 600, color: "#8a7c66", marginTop: 26 }}>stippl.io</div>
		</div>
	</AbsoluteFill>
);

export type DuoPanel = { value: string; name: string; sub: string; photo: string };
export type DuoPinProps = { top: DuoPanel; bottom: DuoPanel; band: string };

/**
 * `pin-duo` — two panels, one contrast.
 *
 * Some findings are a comparison and nothing else. "Bangkok gets 2 nights, Koh Tao gets 4, and the
 * reason is that one is a gateway and the other is a destination" is not a ranking with two entries;
 * flattening it into a bar chart loses the point. The second panel's figure takes the light accent
 * so the pair reads as a comparison rather than a list of two.
 */
export const DuoPin: React.FC<DuoPinProps> = ({ top, bottom, band }) => {
	const Panel: React.FC<{ p: DuoPanel; accent: string }> = ({ p, accent }) => (
		<div style={{ position: "relative", height: 700, overflow: "hidden" }}>
			<Backdrop photo={p.photo} gradient="linear-gradient(90deg, rgba(7,31,38,.86) 0%, rgba(7,31,38,.25) 100%)" />
			<AbsoluteFill style={{ padding: "56px 62px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
				<div style={{ fontSize: 190, lineHeight: 0.86, fontWeight: 700, color: accent, letterSpacing: -8, fontVariantNumeric: "tabular-nums" }}>
					{p.value}
				</div>
				<div style={{ fontSize: 58, fontWeight: 600, color: "#fff", marginTop: 8 }}>{p.name}</div>
				<div style={{ fontSize: 34, color: "rgba(255,255,255,.76)", marginTop: 12 }}>{p.sub}</div>
			</AbsoluteFill>
		</div>
	);

	return (
		<AbsoluteFill style={{ fontFamily, backgroundColor: DEEP }}>
			<Panel p={top} accent="#fff" />
			<Panel p={bottom} accent="#86e0c4" />
			<div
				style={{
					flex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: 28,
					letterSpacing: 2,
					textTransform: "uppercase",
					color: "rgba(255,255,255,.6)",
				}}
			>
				{band}
			</div>
		</AbsoluteFill>
	);
};
